import { Map } from 'immutable'
import { createSelector } from 'reselect'
import { conditionSelector } from 'selectors/feature/conditions'
import { getInOr, getIn, get, safeToJS } from 'utils/immutable'
import { getDependencyKey, calculatePowerNetLevels } from 'utils/simulation'
import { POWER_NET_LEVEL_SIGNAL_URIS } from 'constants/featureOutputs'
import { filter } from 'ramda'

const allConditionSimulationsSelector = getInOr(Map(), [
  'feature',
  'simulations',
  'values',
])

const simulationMetaSelector = getInOr(Map(), [
  'feature',
  'simulations',
  'meta',
])

export const getSimulationMeta = createSelector(
  simulationMetaSelector,
  safeToJS
)

const selectSimulationsByQueryUri = createSelector(
  allConditionSimulationsSelector,
  conditionSelector,
  (_, props) => props,
  (simulations, condition, { queryUri }) => {
    const sim = get(queryUri, simulations)
    if (!sim) {
      return Map()
    }
    const dependencyKey = getDependencyKey(condition)
    if (dependencyKey) {
      const dependency = simulations.get(dependencyKey)
      return sim.merge(Map({ dependency }))
    }

    return sim
  }
)

const selectPowerNetLevelSimulations = createSelector(
  allConditionSimulationsSelector,
  (conditions) => {
    const batteryVoltage = conditions.getIn([
      POWER_NET_LEVEL_SIGNAL_URIS.BATTERY_VOLTAGE,
      'current',
    ])
    const ignitionStatus = conditions.getIn([
      POWER_NET_LEVEL_SIGNAL_URIS.IGNITION_STATUS,
      'current',
    ])
    const engineSpeed = conditions.getIn([
      POWER_NET_LEVEL_SIGNAL_URIS.ENGINE_SPEED,
      'current',
    ])

    const powerNetLevels = calculatePowerNetLevels({
      batteryVoltage,
      ignitionStatus,
      engineSpeed,
    })

    return Map({
      batteryVoltage,
      ignitionStatus,
      engineSpeed,
      powerNetLevels,
    })
  }
)

export const getPowerNetLevelSimulationValues = createSelector(
  selectPowerNetLevelSimulations,
  safeToJS
)

export const getIsActiveSimulatedPowerNetLevel = createSelector(
  selectPowerNetLevelSimulations,
  (_, props) => props,
  (state, { powerNetLevel }) => {
    if (powerNetLevel === undefined) return true
    return state.get('powerNetLevels').includes(powerNetLevel)
  }
)

export const getConditionSimulationValuesByUri = createSelector(
  selectSimulationsByQueryUri,
  (simulation) => {
    const simulatedValue = getIn(['dependency', 'current'], simulation)
    // For edge trigger conditions, use previousBaseResult for evaluation display
    // (the base condition result before edge detection is applied)
    // For non-edge conditions, use evaluation directly
    const hasEdge = simulation.has('edgeEvaluation')
    const evaluationResult = hasEdge
      ? simulation.get('previousBaseResult')
      : simulation.get('evaluation')
    return {
      evaluationResult,
      edgeEvaluation: simulation.get('edgeEvaluation'),
      simulatedValue,
      canReset: simulatedValue !== getIn(['dependency', 'default'], simulation),
    }
  }
)

export const getTimerSimulationValueByUri = createSelector(
  selectSimulationsByQueryUri,
  get('current')
)

export const getNodeCarrySimulationEvaluation = createSelector(
  allConditionSimulationsSelector,
  (_, props) => props,
  (simulations, { queryUri }) =>
    getInOr(
      getInOr(false, [queryUri, 'evaluation'], simulations), // single queries
      [queryUri, 'outputting'], // timers & logic sets
      simulations
    )
)

export const makeConditionSimulationSelector = () =>
  getConditionSimulationValuesByUri

export const makeLogicSetSimulationsSelector = () =>
  createSelector(selectSimulationsByQueryUri, safeToJS)

export const getSimulatedOutputCurrent = createSelector(
  allConditionSimulationsSelector,
  (_, props) => props,
  (simulations, { outputId }) =>
    getInOr(0, [outputId, 'currentAmps'], simulations)
)

export const getIsSimulatedVirtualFuseOpen = createSelector(
  allConditionSimulationsSelector,
  (_, props) => props,
  (simulations, { outputId }) =>
    getInOr(false, [outputId, 'fuseOpen'], simulations)
)

export const getTimeRemainingVirtualFuseReset = createSelector(
  allConditionSimulationsSelector,
  (_, props) => props,
  (simulations, { outputId }) =>
    getInOr(0, [outputId, 'timeRemainingMs'], simulations)
)

export const getSimulatedOpenVirtualFuses = createSelector(
  allConditionSimulationsSelector,
  (simulations) => filter((sim) => sim.get('fuseOpen')).keys()
)
export const getSimulatedVirtualFuseRetries = createSelector(
  allConditionSimulationsSelector,
  (_, props) => props,
  (simulations, { outputId }) => getInOr(0, [outputId, 'retries'], simulations)
)

export default {
  selectSimulationsByQueryUri,
  getConditionSimulationValuesByUri,
  makeConditionSimulationSelector,
  allConditionSimulationsSelector,
  getSimulationMeta,
}
